<script lang="ts">
	import type { IdMap, TransactionCompare_t, Transaction_t } from '../types';
	import SortingHeader from './SortingHeader.svelte';
	import { fmt_amount, fmt_reknr } from '../tools';
	import Modal from './Modal.svelte';
	import { changeTransactionTrefwoord, multiChangeTransactionTrefwoord } from '../api';

	let { mydata } = $props<{
		mydata: {
			id2trefwoord: IdMap<string>;
			trefwoordIds: number[];
			transactions: Transaction_t[];
			totaal: number;
		};
	}>();

	let showModal = $state(false);
	let transIdSet = $state(new Set<number>());

	let sortnr = $state(0);
	let order = $state<1 | -1>(1);

	function headClick(n: number) {
		sortnr = n;
		order *= -1;
	}

	function compare(a: string | number, b: string | number) {
		return a < b ? -order : a > b ? order : 0;
	}

	const sortfunct: TransactionCompare_t[] = [
		(a, b) => compare(a.datum, b.datum),
		(a, b) => compare(a.reknr, b.reknr),
		(a, b) => compare(a.tegenreknr, b.tegenreknr),
		(a, b) => compare(a.type, b.type),
		(a, b) => {
			const ta = mydata.id2trefwoord[a.trefwoordId];
			const tb = mydata.id2trefwoord[b.trefwoordId];
			return compare(ta, tb);
		},
		(a, b) => compare(a.bedrag, b.bedrag)
	];

	const sortedTransactions = $derived([...mydata.transactions].sort(sortfunct[sortnr]));

	let currentTrans = $state({} as Transaction_t);
	let selectedTwId = $state<number>(0);
	let applyTo = $state('one');

	function show(trans: Transaction_t) {
		currentTrans = trans;
		selectedTwId = trans.trefwoordId;
		applyTo = trans.tegenreknr ? 'fromNowOn' : 'one';
		showModal = true;
	}

	function handleClick(transId: number) {
		if (transIdSet.has(transId)) {
			transIdSet = new Set([...transIdSet].filter((id) => id !== transId));
		} else {
			transIdSet = new Set([...transIdSet, transId]);
		}
	}

	async function handleChangeTrefwoord() {
		try {
			await changeTransactionTrefwoord(currentTrans.id, selectedTwId, applyTo);
			showModal = false;
			// Emit an event to refresh the data
			const event = new CustomEvent('dataChanged');
			window.dispatchEvent(event);
		} catch (error) {
			console.error('Failed to change trefwoord:', error);
		}
	}

	let multiSelectedTwId = $state<number>(1);
	async function handleMultiChange() {
		try {
			await multiChangeTransactionTrefwoord(multiSelectedTwId, Array.from(transIdSet));
			transIdSet = new Set();
			// Emit an event to refresh the data
			const event = new CustomEvent('dataChanged');
			window.dispatchEvent(event);
		} catch (error) {
			console.error('Failed to change multiple trefwoorden:', error);
		}
	}
</script>

<div class="content">
	{#if mydata.transactions && mydata.transactions.length > 0}
		<article class="transactions">
			<SortingHeader n={0} {sortnr} {headClick} {order} title="Datum" />
			<SortingHeader n={1} {sortnr} {headClick} {order} title="Rekeningnummer" />
			<SortingHeader n={2} {sortnr} {headClick} {order} title="Tegenrekening" />
			<SortingHeader n={3} {sortnr} {headClick} {order} title="Type" />
			<SortingHeader n={4} {sortnr} {headClick} {order} title="Trefwoord" />
			<SortingHeader n={5} {sortnr} {headClick} {order} title="Bedrag" align="right" />

			{#key order}
				{#each sortedTransactions as line (line.id)}
					<div>{line.datum}</div>
					<div class="clr1">{fmt_reknr(line.reknr)}</div>
					<div class="clr2">{fmt_reknr(line.tegenreknr)}</div>
					<div class="clr1">{line.type}</div>
					<div
						onclick={() => show(line)}
						onkeydown={() => show(line)}
						class="clr2 cursor-pointer trefwoord"
						role="button"
						tabindex="0"
					>
						{mydata.id2trefwoord[line.trefwoordId] || '?'}
					</div>
					<div class="right">{fmt_amount(line.bedrag)}</div>
					<div class="l2">&nbsp;</div>
					<div class="l2 omschr">
						{#if !line.tegenreknr}
							<input type="checkbox" value={line.id} onclick={() => handleClick(line.id)} />
						{/if}
						{line.omschrijving}
					</div>
					<div class="l2">&nbsp;</div>
				{/each}
			{/key}
			<div class="footer spantotal">Totaal</div>
			<div class="footer right">{fmt_amount(mydata.totaal)}</div>
		</article>
		{#if transIdSet.size > 0}
			<div class="controls">
				<select bind:value={multiSelectedTwId}>
					{#each mydata.trefwoordIds as twId (twId)}
						<option value={twId}>{mydata.id2trefwoord[twId]}</option>
					{/each}
				</select>
				<button onclick={handleMultiChange}>Wijzig</button>
			</div>
		{/if}
	{/if}
</div>

<Modal bind:showModal>
	<h3>Overschrijving</h3>
	<p>
		van/naar {fmt_reknr(currentTrans.tegenreknr)}<br />
		{(currentTrans.omschrijving || '').substring(0, 35)}
	</p>
	<h3>Wijzig trefwoord</h3>

	<select class="select-trefw" size="20" bind:value={selectedTwId}>
		{#each mydata.trefwoordIds as twId (twId)}
			<option value={twId}>{mydata.id2trefwoord[twId]}</option>
		{/each}
	</select>
	<h3>Dit trefwoord toepassen op</h3>
	<label>
		<input type="radio" bind:group={applyTo} value="one" />
		alleen op deze overschrijving
	</label>
	{#if currentTrans.tegenreknr}
		<label>
			<input type="radio" bind:group={applyTo} value="fromNowOn" />
			deze en toekomstige overschrijvingen van/naar deze tegenrekening
		</label>
		<label>
			<input type="radio" bind:group={applyTo} value="all" />
			alle overschrijving van/naar deze tegenrekening (ook vorige overschrijvingen aanpassen)
		</label>
	{/if}
	<div class="right buttonbar">
		<button onclick={handleChangeTrefwoord}>Wijzig</button>
		<button onclick={() => (showModal = false)}>Annuleer</button>
	</div>
</Modal>

<style>
	.content {
		padding-bottom: 3em;
	}
	article,
	.transactions {
		width: 90%;
		font-size: var(--fs-100, 1em);
		display: grid;
		grid-template-columns: 7em 15em 15em 5em 10em 7em;
		margin-top: 1em;
	}
	.transactions > div {
		border-bottom: 1px solid black;
		padding: 0 0.4em;
	}
	.clr2 {
		background: #b9d0b8;
	}
	.clr1 {
		background: #dbe9db;
	}
	.omschr {
		grid-column: 2 / 6;
	}
	div.l2 {
		padding-bottom: 1em;
	}
	.footer {
		border-top: 1px solid black;
		border-bottom: 1px solid black;
		padding: 0 0.4em;
		font-weight: bold;
		background: #ccc;
	}
	.spantotal {
		grid-column: 1/6;
	}
	.trefwoord:hover {
		background: yellow;
	}
	.select-trefw {
		margin-bottom: 1em;
		height: 27em;
		max-height: calc(100vh - 35em);
	}
	label {
		display: grid;
		grid-template-columns: 2em auto;
		margin-bottom: 0.5em;
	}
	.right {
		text-align: right;
	}
	.cursor-pointer {
		cursor: pointer;
	}
	.controls {
		position: absolute;
		top: 3em;
		right: 2em;
		background: #999;
		border: 1px solid #aaa;
		padding: 0.5em 1em;
		margin-top: 1em;
		display: flex;
		gap: 1em;
		align-items: center;
	}
	.buttonbar {
		display: flex;
		gap: 1em;
		margin-top: 1em;
	}
	input[type='radio'] {
		width: 1em;
		height: 1em;
		margin-top: 0.4em;
	}
</style>
